import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Eye, 
  Flag, 
  GraduationCap, 
  Users, 
  Trophy, 
  Clock, 
  CheckCircle, 
  Star, 
  TrendingUp, 
  Shield, 
  HeadphonesIcon, 
  Zap,
  BookOpen,
  Award,
  Heart,
  Target
} from 'lucide-react';
import { Button } from '../../components/ui/button.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card.jsx';
import { Badge } from '../../components/ui/badge.jsx';

const About = () => {
  const navigate = useNavigate();
  const stats = [
    { 
      icon: GraduationCap, 
      value: '29+', 
      label: 'Years of Excellence',
      description: 'Dedicated to education',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    { 
      icon: Users, 
      value: '500+', 
      label: 'Students',
      description: 'Active learners',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    { 
      icon: BookOpen, 
      value: '50+', 
      label: 'Teachers',
      description: 'Expert educators',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    { 
      icon: Trophy, 
      value: '95%', 
      label: 'Success Rate',
      description: 'Academic excellence',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
  ];

  const values = [
    {
      icon: Eye,
      title: 'Vision',
      description: 'To be the leading educational institution that nurtures future leaders and innovators.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      icon: Flag,
      title: 'Mission',
      description: 'To provide quality education that develops character, critical thinking, and creativity.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    {
      icon: Heart,
      title: 'Values',
      description: 'Integrity, excellence, respect, and service to humanity guide everything we do.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
  ];

  const features = [
    {
      icon: BookOpen,
      title: 'Quality Education',
      description: 'Comprehensive curriculum designed to meet international standards and prepare students for success.',
      color: 'text-blue-600'
    },
    {
      icon: Award,
      title: 'Experienced Faculty',
      description: 'Dedicated teachers with years of experience and continuous professional development.',
      color: 'text-emerald-600'
    },
    {
      icon: Shield,
      title: 'Safe Environment',
      description: 'Secure campus with modern facilities and a nurturing atmosphere for learning.',
      color: 'text-purple-600'
    },
    {
      icon: Target,
      title: 'Holistic Development',
      description: 'Focus on academic excellence, character building, and extracurricular activities.',
      color: 'text-orange-600'
    },
    {
      icon: Zap,
      title: 'Modern Technology',
      description: 'State-of-the-art learning resources and digital tools for enhanced education.',
      color: 'text-red-600'
    },
    {
      icon: HeadphonesIcon,
      title: 'Student Support',
      description: 'Comprehensive support services including counseling, tutoring, and career guidance.',
      color: 'text-indigo-600'
    },
  ];

  const timeline = [
    {
      year: '1995',
      title: 'Foundation',
      description: 'Bridge of Life Academy was established with a vision to provide quality education.',
      color: 'bg-blue-500'
    },
    {
      year: '2005',
      title: 'Expansion',
      description: 'Added secondary school section and modern facilities to accommodate growing student population.',
      color: 'bg-emerald-500'
    },
    {
      year: '2015',
      title: 'Digital Transformation',
      description: 'Implemented modern technology and digital learning platforms for enhanced education.',
      color: 'bg-purple-500'
    },
    {
      year: '2020',
      title: 'Recognition',
      description: 'Awarded as one of the top performing schools in the region for academic excellence.',
      color: 'bg-orange-500'
    },
    {
      year: '2025',
      title: 'Future Ready',
      description: 'Continuing to innovate and prepare students for the challenges of tomorrow.',
      color: 'bg-red-500'
    },
  ];

  const testimonials = [
    {
      name: 'Mrs. Adebayo',
      role: 'Parent',
      content: 'Bridge of Life Academy has been instrumental in my child\'s academic and personal growth. The teachers are exceptional.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Mr. Johnson',
      role: 'Parent',
      content: 'The holistic approach to education here is remarkable. My daughter loves going to school every day.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Mrs. Okafor',
      role: 'Parent',
      content: 'Excellent academic standards and wonderful character development programs. Highly recommended!',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge variant="outline" className="mb-6 bg-white/10 text-white border-white/20">
              About Us
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About Bridge of Life Academy
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              For over 29 years, we have been committed to providing quality education that shapes 
              young minds and prepares them for a successful future.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="text-center hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className={`w-16 h-16 ${stat.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                        <Icon className={`w-8 h-8 ${stat.color}`} />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</h3>
                      <p className="text-lg font-semibold text-gray-700 mb-1">{stat.label}</p>
                      <p className="text-sm text-gray-500">{stat.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision, Mission, Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Foundation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on strong principles and a clear vision for educational excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full text-center hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-8">
                      <div className={`w-16 h-16 ${value.bgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <Icon className={`w-8 h-8 ${value.color}`} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{value.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide a comprehensive educational experience that goes beyond academics
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="mb-4">
                        <Icon className={`w-12 h-12 ${feature.color}`} />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A timeline of our growth and achievements over the years
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gray-200"></div>
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <Card className="hover:shadow-lg transition-shadow duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-center mb-3">
                          <div className={`w-4 h-4 ${item.color} rounded-full mr-3`}></div>
                          <h3 className="text-2xl font-bold text-gray-900">{item.year}</h3>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h4>
                        <p className="text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="w-8 h-8 bg-white border-4 border-blue-600 rounded-full flex items-center justify-center z-10">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Parents Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from the parents who trust us with their children's education
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-6 italic">"{testimonial.content}"</p>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gray-300 rounded-full mr-4 flex-shrink-0"></div>
                      <div>
                        <p className="font-semibold text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Join Our School Family
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Be part of our legacy of excellence in education. Contact us today to learn more 
              about our admission process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-gray-100"
                onClick={() => navigate('/contact')}
              >
                Apply Now
                <TrendingUp className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-blue-900"
                onClick={() => navigate('/contact')}
              >
                Schedule Visit
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;